import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Grid, Skeleton, Typography } from "@mui/material";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import { isNumeric } from "@/Common/helpers/Verifications/numbers";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import Button from "@/Layouts/components/Buttons/Button";
import DoubleCheckingButton from "@/Layouts/components/Buttons/DoubleCheckingButton";

// Interfaces.
import {
	ISalesPerformanceReportVersion,
	ISalesPerformanceReportFormatsModelProps,
} from "@/Settings/types/SalesPerformanceReportFormats/model";
import {
	ISalesPerformanceFormatsVersionProps,
	IRenderButtonOrSkeletonProps,
	IProduct,
} from "@/Settings/types/SalesPerformanceReportFormats/utils";

import { errorLogger } from "@/Common/helpers/logger";
import { TInputData } from "@/Layouts/types/InputComponents/InputFormExtension";
import { NumberOrString } from "@/Layouts/components/InputComponents/InputValue/types";

const montserratFs18 = "!font-montserrat !font-medium !text-[18px] text-default";
const regularSansLabel = "!font-sans !text-[14.5px] text-default";
const selectAnOptionLabel = "Select an option";

const productsList = () => {
	return [
		{ option: "ST/BR" },
		{ option: "JRNL-DATE" },
		{ option: "REF NO" },
		{ option: "SLM-NO" },
		{ option: "SLS-NO" },
		{ option: "DOC FEE" },
		{ option: "FIN-MGR-NO" },
		{ option: "CUST-NAME" },
		{ option: "CUST-NO" },
		{ option: "UWCER" },
		{ option: "STK-NO" },
		{ option: "DESC" },
		{ option: "SALE-TYPE" },
		{ option: "GROSS PROFIT" },
		{ option: "GAP/AFTMKT/MAINT" },
		{ option: "WARRANTY RES" },
		{ option: "F&I RESERVE" },
		{ option: "TOTAL GROSS" },
		{ option: "UNITS" },
		{ option: "SLM-COMM" },
		{ option: "COMM-CODE" },
		{ option: "COMM-REMARK" },
		{ option: "SALE-AMT" },
	];
};

const newProduct = () => {
	return {
		_id: "",
		label: "",
		reportField: "",
		contractUnits: 1.0,
	};
};

const renderSalesPerformanceSection = (props: ISalesPerformanceReportFormatsModelProps) => {
	return (
		<Grid container item xs={12} alignItems="center" columnSpacing={2}>
			<Grid item xs={6}>
				{props.loading ? (
					<Skeleton variant="rectangular" width={180} height={30} sx={{ borderRadius: "6px" }} />
				) : (
					<Typography className={montserratFs18}>RTC Data Point</Typography>
				)}
			</Grid>
			<Grid item xs={6}>
				{props.loading ? (
					<Skeleton variant="rectangular" width={200} height={30} sx={{ borderRadius: "6px" }} />
				) : (
					<Typography className={montserratFs18} align="center">
						Sales Performance Report
					</Typography>
				)}
			</Grid>
		</Grid>
	);
};

const isError = (props: Readonly<ISalesPerformanceReportFormatsModelProps>) => {
	return props.error ?? false;
};

const renderLabelSection = (loading: boolean, label: string, className: string) => {
	return loading ? <Skeleton variant="text" width={120} height={20} /> : <Typography className={className}>{label}</Typography>;
};

const renderButtonOrSkeleton = ({ loading, addProduct, disabled }: IRenderButtonOrSkeletonProps) => {
	if (loading) {
		return <Skeleton variant="rectangular" width={160} height={28} sx={{ borderRadius: "6px" }} />;
	}

	return (
		<Button
			variant="contained"
			text="Add Back Product"
			classes="tier-btn tier-btn-free-width add-tier-btn"
			disabled={disabled}
			onClick={() => addProduct("backProducts")}
			bgColor={"var(--primary-color)"}
			endIcon={<AddCircleRounded />}
		/>
	);
};

const renderProductsLabelSection = (loading: boolean, width: number, height: number, text: string, isMsg: boolean) => {
	if (loading) {
		return <Skeleton variant="rectangular" width={width} height={height} sx={{ borderRadius: "6px" }} />;
	}

	return (
		<Typography className={!isMsg ? montserratFs18 : regularSansLabel} textAlign="center">
			{text}
		</Typography>
	);
};

const renderAdvancedSelect = ({
	model,
	field,
	props,
	options,
	setModel,
	propagate,
}: {
	model: ISalesPerformanceReportVersion | null;
	field: string;
	props: ISalesPerformanceReportFormatsModelProps;
	options: object[];
	setModel: (model: ISalesPerformanceReportVersion | null) => void;
	propagate: (model: ISalesPerformanceReportVersion) => void;
}) => {
	const handleChangeOption = (selected: { option: string } | null) => {
		if (!model) {
			return false;
		}

		const currentModel = { ...model };

		if (selected && field !== "frontProducts" && field !== "backProducts") {
			currentModel[field] = selected.option;
		}

		setModel(currentModel);
		propagate(currentModel);
		return true;
	};

	const getSelectedOption = (refModel: ISalesPerformanceReportVersion | null, refField: string) => {
		const option = refModel?.[refField as keyof ISalesPerformanceReportVersion];
		return option ? { option } : null;
	};

	return props.loading ? (
		<Skeleton variant="rectangular" width={250} height={30} sx={{ borderRadius: "6px" }} />
	) : (
		<SmartSelector
			showBy="option"
			selectBy="option"
			options={options || []}
			selected={getSelectedOption(model, field)}
			// selected={
			// 	model ? (model[field as keyof ISalesPerformanceReportVersion] as NonNullable<string | object | null | undefined>) : {}
			// }
			inputProps={{
				placeholder: "Select an option",
			}}
			listBoxProps={{
				sx: { maxHeight: "220px" },
			}}
			disabled={props.disabled}
			error={!!props.error}
			sx={{ minWidth: "100%" }}
			onChangeAction={(selected) => handleChangeOption(selected as { option: string })}
		/>
	);
};

const printError = (error: unknown): void => {
	if (error instanceof Error) {
		errorLogger(error.message);
	} else {
		errorLogger("An unknown error occurred");
	}
};

export default function SalesPerformanceFormatsVersion(props: Readonly<ISalesPerformanceFormatsVersionProps>) {
	const [options, setOptions] = useState<{ option: string }[]>([]);
	const [model, setModel] = useState<ISalesPerformanceReportVersion | null>(null);
	const [disabledAdd, setDisabledAdd] = useState(false);
	const queryClient = useQueryClient();
	const fixedProducts = 12;

	const addProduct = (area: "frontProducts" | "backProducts") => {
		if (!model) {
			return false;
		}

		const currentModel = { ...model };
		const currentOptions = [...options];

		try {
			currentModel[area].push(newProduct());

			const combined = currentModel["frontProducts"].length + currentModel["backProducts"].length;
			const totalPermissibles = currentOptions.length - fixedProducts;

			if (combined >= totalPermissibles) {
				setDisabledAdd(true);
			} else {
				setDisabledAdd(false);
			}
		} catch (error: unknown) {
			printError(error);
		}

		setModel(currentModel);
		return true;
	};

	const deleteProduct = (area: "frontProducts" | "backProducts", index: number) => {
		if (!model) {
			return false;
		}

		const currentModel = { ...model };
		const currentOptions = [...options];

		try {
			if (index > -1) {
				currentModel[area].splice(index, 1);

				const totalPermissibles = currentOptions.length - fixedProducts;
				const combined = currentModel["frontProducts"].length + currentModel["backProducts"].length;
				if (combined < totalPermissibles) {
					setDisabledAdd(false);
				}
			}
		} catch (error: unknown) {
			printError(error);
		}

		setModel(currentModel);
		propagate(currentModel);

		return true;
	};

	const handleChangeOption = (selected: { option: string } | null, area: keyof ISalesPerformanceReportVersion, index = 0) => {
		if (!model) {
			return false;
		}

		const currentModel = { ...model };

		if (selected) {
			if (area !== "frontProducts" && area !== "backProducts") {
				currentModel[area] = selected.option;
			} else {
				currentModel[area][index]["reportField"] = selected.option;
			}
		} else if (area !== "frontProducts" && area !== "backProducts") {
			currentModel[area] = "";
		} else {
			currentModel[area][index]["reportField"] = "";
		}

		setModel(currentModel);
		propagate(currentModel);

		return true;
	};

	const handleChangeProductLabel = (area: "frontProducts" | "backProducts", index: number, label: string) => {
		if (!model) {
			return false;
		}

		const currentModel = { ...model };
		let update = false;
		try {
			if (currentModel[area][index]["label"] !== label) {
				currentModel[area][index]["label"] = label;
				update = true;
			}
		} catch (error: unknown) {
			printError(error);
		}

		setModel(currentModel);
		if (update) {
			propagate(currentModel);
		}

		return true;
	};

	const handleChangeContractUnits = (area: "frontProducts" | "backProducts", index: number, contractUnits: string | number) => {
		if (!model) {
			return false;
		}

		const currentModel = { ...model };
		if (isNumeric(contractUnits)) {
			const contractUnitsNumber = Number(contractUnits);
			contractUnits = contractUnitsNumber.toFixed(2);
			let update = false;
			try {
				if (Number(currentModel[area][index]["contractUnits"]) !== contractUnitsNumber) {
					currentModel[area][index]["contractUnits"] = Number(contractUnits);
					update = true;
				}
			} catch (error: unknown) {
				printError(error);
			}

			setModel(currentModel);
			if (update) {
				propagate(currentModel);
			}
		}

		return true;
	};

	const propagate = (updatedModel: ISalesPerformanceReportVersion) => {
		if (props.onActionUpdate) {
			props.onActionUpdate(updatedModel);
		}
	};

	const renderGridItem = (labelText: string, fieldName: string) => {
		return (
			<Grid container item xs={12} mt={2} alignItems="center" columnSpacing={2}>
				<Grid item xs={6}>
					{renderLabelSection(Boolean(props.loading), labelText, regularSansLabel)}
				</Grid>
				<Grid item xs={6}>
					{renderAdvancedSelect({
						model,
						props,
						options,
						setModel,
						propagate,
						field: fieldName,
					})}
				</Grid>
			</Grid>
		);
	};

	const renderProductGridLabel = (labelText: string, width: number, height: number) => {
		return props.loading ? (
			<Skeleton variant="text" width={width} height={height} />
		) : (
			<Typography className={montserratFs18}>{labelText}</Typography>
		);
	};

	const renderProductGridItem = (labelText: string, width: number, height: number) => {
		return (
			<Grid item xs={4}>
				{props.loading ? (
					<Skeleton variant="rectangular" width={width} height={height} sx={{ borderRadius: "6px" }} />
				) : (
					<Typography className={montserratFs18} textAlign="center">
						{labelText}
					</Typography>
				)}
			</Grid>
		);
	};

	const renderTextField = (product: IProduct, key: number, productType: "frontProducts" | "backProducts") => {
		return props.loading ? (
			<Skeleton variant="rectangular" width={250} height={30} sx={{ borderRadius: "6px" }} />
		) : (
			<SimpleTextField
				editable={!props.disabled}
				outlined
				text={product.label ?? ""}
				disabled={props.disabled || disabledAdd}
				onActionUpdate={(title: string) => (productType ? handleChangeProductLabel(productType, key, title) : null)}
			/>
		);
	};

	const renderInputForm = (product: IProduct, key: number, productType: "frontProducts" | "backProducts") => {
		return props.loading ? (
			<Skeleton variant="rectangular" width={250} height={30} sx={{ borderRadius: "6px" }} />
		) : (
			<Grid item sx={{ textAlign: "center" }}>
				<InputFormExtension
					readOnly={props.disabled || disabledAdd}
					value={product?.contractUnits ?? 0}
					data={[{ type: "Unit", min: 0, max: 5, decimals: 2, decimalsEditable: true, disabled: props.disabled }] as TInputData[]}
					sx={{ maxWidth: "63px" }}
					onActionUpdate={(contractUnits: NumberOrString) =>
						productType ? handleChangeContractUnits(productType, key, contractUnits) : null
					}
				/>
			</Grid>
		);
	};

	const getSelectedProducts = () => {
		const cachedData = queryClient.getQueryData<ISalesPerformanceReportVersion>([
			"salesPerformanceReportVersion",
			props.locationId,
			props.model?._id,
		]);
		let cachedProducts: string[] = [];

		if (cachedData?.frontProducts.length || cachedData?.backProducts.length) {
			const frontProductFields = cachedData?.frontProducts.map((product) => product.reportField);
			const backProductFields = cachedData?.backProducts.map((product) => product.reportField);
			const combinedProductFields = [...frontProductFields, ...backProductFields].filter(Boolean);
			cachedProducts = Array.from(new Set(combinedProductFields));
		}

		return cachedProducts;
	};

	const renderSelect = (product: IProduct, key: number, productType: string | null) => {
		return props.loading ? (
			<Skeleton variant="rectangular" width={250} height={30} sx={{ borderRadius: "6px" }} />
		) : (
			<SmartSelector
				showBy={"option"}
				selectBy={"option"}
				options={options || []}
				disabledOptions={getSelectedProducts()}
				selected={product.reportField ? { option: product.reportField } : null}
				inputProps={{
					placeholder: selectAnOptionLabel,
				}}
				listBoxProps={{
					sx: { maxHeight: "220px" },
				}}
				clearable
				disabled={props.disabled || disabledAdd}
				error={isError(props)}
				sx={{ minWidth: "100%" }}
				onChangeAction={(selected) => (productType ? handleChangeOption(selected as { option: string }, productType, key) : null)}
			/>
		);
	};

	const renderRemoveButton = (key: number, productType: "frontProducts" | "backProducts") => {
		return props.loading ? (
			<Skeleton variant="circular" width={35} height={35} />
		) : (
			<DoubleCheckingButton
				disabled={props.disabled}
				title={"Remove"}
				icon={<CloseRoundedIcon />}
				iconHover={<CloseRoundedIcon />}
				endIcon={<DeleteOutlineOutlinedIcon sx={{ marginLeft: "-4px" }} />}
				mainButtonClasses={"btn-simple-remove btn-double-remove-regular"}
				mainSx={{ marginLeft: "0px" }}
				confirmationSx={{ left: "5px" }}
				onDeleteAction={() => (productType ? deleteProduct(productType, key) : null)}
			/>
		);
	};

	const renderGridInputsItem = (type: string, product: IProduct, key: number, productType: "frontProducts" | "backProducts") => {
		switch (type) {
			case "textField":
				return productType ? renderTextField(product, key, productType) : null;
			case "inputForm":
				return productType ? renderInputForm(product, key, productType) : null;
			case "select":
				return productType ? renderSelect(product, key, productType) : null;
			case "button":
				return productType ? renderRemoveButton(key, productType) : null;
			default:
				return null;
		}
	};

	const renderProductGrids = (productType: "frontProducts" | "backProducts") => {
		const products = model?.[productType];
		return (
			<>
				{products?.map((product, key) => (
					<Grid key={product._id} container item xs={12} alignItems="center" columnSpacing={2} sx={{ position: "relative" }}>
						<Grid item xs={4}>
							{renderGridInputsItem("textField", product, key, productType)}
						</Grid>
						<Grid item xs={4}>
							{renderGridInputsItem("inputForm", product, key, productType)}
						</Grid>
						<Grid item xs={4}>
							{renderGridInputsItem("select", product, key, productType)}
						</Grid>
						<Grid item sx={{ position: "absolute", right: "-50px" }}>
							{renderGridInputsItem("button", product, key, productType)}
						</Grid>
					</Grid>
				))}
			</>
		);
	};

	useEffect(() => {
		let isMounted = true;

		if (isMounted && props.model) {
			setModel(props.model);
			setOptions(productsList());
		}

		return () => {
			isMounted = false;
			setModel(null);
		};
	}, [props]);

	return (
		<Grid container mt={1} mb={1} ml={0.2} style={{ border: "0px solid black" }}>
			<Grid item container xs={12} md={6}>
				{renderSalesPerformanceSection(props)}
				{renderGridItem("Sale Date", "saleDate")}
				{renderGridItem("Deal Number", "dealNumber")}
				{renderGridItem("Customer Name", "customerName")}
				{renderGridItem("Customer Number", "customerNumber")}
				{renderGridItem("Stock Number", "stockNumber")}
				{renderGridItem("Description", "description")}
				{renderGridItem("Sale Type", "saleType")}
				{renderGridItem("Sale Amount", "saleAmount")}
				{renderGridItem("Deal Type", "dealType")}
				{renderGridItem("Units", "units")}
				{renderGridItem("Sales Employee ID", "salesEmployeeId")}
				{renderGridItem("Finance Employee ID", "financeEmployeeId")}
				{renderGridItem("Sales Manager ID", "salesManagerId")}
			</Grid>
			<Grid container item xs={12} md={9} mt={5} alignItems="center">
				<Grid container item mb={1.3}>
					{renderProductGridLabel("Front-end Products Gross Profit", 300, 28)}
				</Grid>

				<Grid container item xs={12} alignItems="center" columnSpacing={2}>
					{renderProductGridItem("RTC Product", 180, 30)}
					{renderProductGridItem("Contract Units", 200, 30)}
					{renderProductGridItem("Dealer Product", 200, 30)}
				</Grid>

				<Grid container item alignItems={"center"} rowSpacing={2} sx={{ paddingTop: "15px" }}>
					{model?.frontProducts.length ? (
						<>{renderProductGrids("frontProducts")}</>
					) : (
						<Grid container item justifyContent={"center"}>
							{props.loading ? (
								<Skeleton variant="text" width={250} height={15} />
							) : (
								<Typography className={regularSansLabel}>No Front-end Products.</Typography>
							)}
						</Grid>
					)}
				</Grid>

				<Grid container item mt={2.5} justifyContent={"center"}>
					{props.loading ? (
						<Skeleton variant="rectangular" width={160} height={28} sx={{ borderRadius: "6px" }} />
					) : (
						<Button
							variant="contained"
							text="Add Front Product"
							classes="tier-btn tier-btn-free-width add-tier-btn"
							disabled={disabledAdd || props.disabled}
							onClick={() => {
								addProduct("frontProducts");
							}}
							bgColor={"var(--primary-color)"}
							endIcon={<AddCircleRounded />}
						/>
					)}
				</Grid>
			</Grid>
			<Grid container item xs={0} md={3} />
			<Grid container item xs={12} md={9} mt={3} mb={3} alignItems="center">
				<Grid container item mb={1.3}>
					{renderProductsLabelSection(Boolean(props.loading), 300, 28, "Back-end Products Gross Profit", false)}
				</Grid>
				<Grid container item xs={12} alignItems="center" columnSpacing={2}>
					<Grid item xs={4}>
						{renderProductsLabelSection(Boolean(props.loading), 180, 30, "RTC Product", false)}
					</Grid>
					<Grid item xs={4}>
						{renderProductsLabelSection(Boolean(props.loading), 200, 30, "Contract Units", false)}
					</Grid>
					<Grid item xs={4}>
						{renderProductsLabelSection(Boolean(props.loading), 200, 30, "Dealer Product", false)}
					</Grid>
				</Grid>

				<Grid container item alignItems={"center"} rowSpacing={2} sx={{ paddingTop: "15px" }}>
					{model?.backProducts.length ? (
						<>{renderProductGrids("backProducts")}</>
					) : (
						<Grid container item justifyContent={"center"}>
							{renderProductsLabelSection(Boolean(props.loading), 250, 15, "No Back-end Products.", true)}
						</Grid>
					)}
				</Grid>

				<Grid container item mt={2.5} justifyContent={"center"}>
					{renderButtonOrSkeleton({
						addProduct,
						loading: props.loading,
						disabled: props.disabled || disabledAdd,
					})}
				</Grid>
			</Grid>
			<Grid container item xs={0} md={3} />
		</Grid>
	);
}
